import { useState, useCallback } from 'react';
import { OV_STRINGS } from '../constants/strings';
import * as ovApi from '../apis/ownerVerificationApi';
import { uploadFileToAurora } from '../../../services/upload/uploadService';
import { toast, TOAST_POSITION } from '../../../components/material/ToastPopup';
import { useTicketPurchase } from '../../../features/ticketPurchase';
import { OV_SERVICE_NAME } from '../constants/constants';

export const STEP = {
  LOADING: 'loading',
  CONFIRM: 'confirm',
  PURCHASE: 'purchase',
  DETAIL: 'detail',
  NOTICE: 'notice',
  RECIPIENT: 'recipient',
  IN_REVIEW: 'in_review',
  COMPLETED: 'completed',
  EXPIRED: 'expired',
  FAILED: 'failed',
  REJECTED: 'rejected',
  EXIT_CONFIRM: 'exit',
  DONE: 'done',
  MEMBERSHIP_REQUIRED: 'membership_required',
};

export default function useOwnerVerification() {
  const [step, setStep] = useState(STEP.LOADING);
  const [loading, setLoading] = useState(false);
  const [estate, setEstate] = useState(null);
  const [saleUuid, setSaleUuid] = useState(null);
  const [draftUuid, setDraftUuid] = useState(null);
  const [detailData, setDetailData] = useState({ dong: '', floor: '', ho: '' });
  const [verificationData, setVerificationData] = useState(null);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [ticketQuantity, setTicketQuantity] = useState(1);

  const showToast = useCallback((msg) => {
    toast.error(msg, { position: TOAST_POSITION.BOTTOM_CENTER });
  }, []);

  const ticket = useTicketPurchase({ serviceName: OV_SERVICE_NAME, showToast });

  const startVerification = useCallback(async (uuid, { skipStatusCheck = false, verificationUuid = null } = {}) => {
    setSaleUuid(uuid);
    setStep(STEP.LOADING);
    setLoading(true);

    try {
      const { sale: estateData } = await ovApi.getSaleInfo(uuid);
      setEstate(estateData);


      if (!skipStatusCheck) try {
        const statusData = await ovApi.getVerificationStatus(uuid, verificationUuid);
        if (statusData.status === 'success' && statusData.uuid) {
          setDraftUuid(statusData.uuid);
          const vs = statusData.verification_status;

          if (vs !== 'DRAFT') {
            try {
              const attachData = await ovApi.listAttachments(statusData.uuid);
              if (attachData.status === 'success') {
                statusData.attachments = attachData.attachments || [];
              }
            } catch (e) {
              console.error('첨부파일 목록 조회 에러:', e);
            }
          }

          setVerificationData(statusData);

          if (vs === 'DRAFT') {}
          else if (vs === 'PENDING' || vs === 'AWAITING_REVIEW') { setStep(STEP.IN_REVIEW); return; }
          else if (vs === 'COMPLETED') { setStep(statusData.validity_active ? STEP.COMPLETED : STEP.EXPIRED); return; }
          else if (vs === 'FAILED') { setStep(STEP.FAILED); return; }
          else if (vs === 'REJECTED') { setStep(STEP.REJECTED); return; }
          else { setStep(STEP.IN_REVIEW); return; }
        }
      } catch (e) {
        console.error('인증 상태 조회 에러:', e);
      }


      if (!window.loggedInUser?.isInit || !window.loggedInUser?.getInfo()?.membership) {
        setStep(STEP.MEMBERSHIP_REQUIRED);
        return;
      }

      await ticket.checkTicket();
      setStep(STEP.CONFIRM);
    } catch (e) {
      console.error('[OV] startVerification error:', e);
      showToast(OV_STRINGS.ERROR_TICKET_CHECK_FAIL);
    } finally {
      setLoading(false);
    }
  }, [showToast, ticket]);


  const handleStart = useCallback(async (requiredTickets) => {
    if (!saleUuid || !estate) return;
    setLoading(true);
    if (requiredTickets) setTicketQuantity(requiredTickets);

    try {
      const hasTicket = await ticket.checkTicket();
      if (!hasTicket) { setStep(STEP.PURCHASE); return; }

      const draft = await ovApi.createDraft({
        sale_uuid: saleUuid,
        pnu: String(estate.pnu),
        address: estate.address,
        ticket_quantity: requiredTickets || ticketQuantity,
      });

      if (draft.status !== 'success') {
        showToast(OV_STRINGS.ERROR_INITIATE_FAIL);
        return;
      }

      setDraftUuid(draft.uuid);
      setDetailData({ dong: '', floor: '', ho: '' });
      setStep(STEP.DETAIL);
    } catch (e) {
      const reason = e.body?.reason;
      const reasonMessages = {
        verification_in_progress: OV_STRINGS.ERROR_VERIFICATION_IN_PROGRESS,
        verification_rejected: OV_STRINGS.ERROR_VERIFICATION_REJECTED,
        renewal_not_available: OV_STRINGS.ERROR_RENEWAL_NOT_AVAILABLE,
      };
      showToast(reasonMessages[reason] || OV_STRINGS.ERROR_INITIATE_FAIL);
    } finally {
      setLoading(false);
    }
  }, [saleUuid, estate, ticketQuantity, showToast, ticket]);


  const handleDetailNext = useCallback((data) => {
    setDetailData(data);
    setStep(STEP.NOTICE);
  }, []);


  const handleNoticeNext = useCallback(() => {
    setStep(STEP.RECIPIENT);
  }, []);


  const handleSend = useCallback(async (recipientData, files) => {
    if (!draftUuid) return;
    setLoading(true);

    try {
      const hasTicket = await ticket.checkTicket();
      if (!hasTicket) { setStep(STEP.PURCHASE); return; }

      const result = await ovApi.initiate(draftUuid, {
        pnu: detailData.pnu || String(estate.pnu),
        address: detailData.address || estate.address,
        pin: detailData.pin,
        dong: detailData.dong,
        floor: detailData.floor,
        ho: detailData.ho,
        ticket_quantity: ticketQuantity,
        ...recipientData,
      });

      if (result.status !== 'success') {
        showToast(OV_STRINGS.ERROR_SEND_FAIL);
        return result;
      }


      if (files?.length) {
        for (const file of files) {
          try {
            const saveName = await uploadFileToAurora('ov_attachment', file, draftUuid);
            if (saveName) {
              await ovApi.registerAttachment(draftUuid, {
                saveName,
                originalFilename: file.name,
                fileSize: file.size,
                mimeType: file.type || 'application/octet-stream',
              });
            }
          } catch (e) {
            console.error('파일 업로드 실패:', e);
            showToast('일부 파일 업로드에 실패했습니다.');
          }
        }
      }

      setStep(STEP.DONE);
      return result;
    } catch {
      showToast(OV_STRINGS.ERROR_SEND_FAIL);
    } finally {
      setLoading(false);
    }
  }, [draftUuid, detailData, estate, ticketQuantity, showToast, ticket]);


  const handleCharge = useCallback(() => {
    setStep(STEP.PURCHASE);
  }, []);


  const handleCloseAttempt = useCallback(() => {
    if (draftUuid && (step === STEP.DETAIL || step === STEP.NOTICE || step === STEP.RECIPIENT)) {
      setShowExitConfirm(true);
    } else {
      return 'close';
    }
  }, [draftUuid, step]);


  const handleExitConfirm = useCallback(() => {
    setShowExitConfirm(false);
    return 'close';
  }, []);


  const handleExitBack = useCallback(() => {
    setShowExitConfirm(false);
  }, []);


  const handleBack = useCallback(() => {
    switch (step) {
      case STEP.DETAIL: setStep(STEP.CONFIRM); break;
      case STEP.NOTICE: setStep(STEP.DETAIL); break;
      case STEP.RECIPIENT: setStep(STEP.NOTICE); break;
      default: break;
    }
  }, [step]);

  return {
    step,
    ticketCount: ticket.ticketCount,
    ticketProduct: ticket.ticketProduct,
    loading,
    estate,
    saleUuid,
    draftUuid,
    detailData,
    verificationData,
    setVerificationData,
    showExitConfirm,
    ticketQuantity,

    startVerification,
    handleStart,
    handleDetailNext,
    handleNoticeNext,
    handleSend,
    handleCharge,
    handleCloseAttempt,
    handleExitConfirm,
    handleExitBack,
    handleBack,
  };
}
